import { useState, useRef, useCallback, useEffect } from 'react';
import {
    UploadCloud, FileText, FileSpreadsheet, FileJson, File,
    Trash2, RefreshCw, Table, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, getStoredToken } from '@/api';
import { useAuth } from '@/context/useAuth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface FileSourceNodeConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

const FILE_TYPE_ICON: Record<string, any> = {
    csv: FileText,
    xlsx: FileSpreadsheet,
    xls: FileSpreadsheet,
    json: FileJson,
    parquet: File,
};

const FILE_TYPE_COLOR: Record<string, string> = {
    csv: 'text-green-600',
    xlsx: 'text-emerald-600',
    xls: 'text-emerald-600',
    json: 'text-amber-600',
    parquet: 'text-purple-600',
};

export const FileSourceNodeConfig = ({ node, onChangeData }: FileSourceNodeConfigProps) => {
    const { token } = useAuth();
    const data = node.data || {};

    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [sheetLoading, setSheetLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fileType = (data.fileType || '').toLowerCase();
    const isExcel = fileType === 'xlsx' || fileType === 'xls';
    const isCsv = fileType === 'csv';
    const Icon = FILE_TYPE_ICON[fileType] || File;
    const iconColor = FILE_TYPE_COLOR[fileType] || 'text-gray-500';

    // ── Upload handler ──────────────────────────────────────────────────
    const handleUpload = useCallback(async (file: File) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const ALLOWED = new Set(['csv', 'xlsx', 'xls', 'json', 'parquet']);
        if (!ALLOWED.has(ext)) { toast.error(`Unsupported file type: .${ext}`); return; }

        const tok = token || getStoredToken();
        if (!tok) { toast.error('Not logged in.'); return; }

        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const result = await apiFetch('/flows/ops/upload', tok, { method: 'POST', body: form });

            onChangeData({
                connectionMode: 'file',
                filePath: result.filePath,
                fileName: result.fileName ?? file.name,
                fileType: result.fileType ?? ext,
                rowCount: result.rowCount ?? 0,
                columns: (result.columns ?? []).map((c: string) => ({ name: c, type: 'string' })),
                outputSchema: (result.columns ?? []).map((c: string) => ({ name: c, type: 'string' })),
                preview: result.preview ?? [],
                sheets: result.sheets ?? [],
                activeSheet: result.activeSheet ?? '',
                label: result.fileName ?? file.name,
            });
            toast.success(`Loaded ${result.columns?.length ?? 0} columns from ${result.fileName ?? file.name}`);
        } catch (err: any) {
            toast.error(`Upload failed: ${err?.message || 'Unknown error'}`);
        } finally {
            setUploading(false);
        }
    }, [token, onChangeData]);

    // ── Sheet change (Excel only) ───────────────────────────────────────
    const handleSheetChange = useCallback(async (sheet: string) => {
        const tok = token || getStoredToken();
        if (!tok || !data.filePath) return;

        setSheetLoading(true);
        try {
            const result = await apiFetch(
                `/flows/ops/upload/sheet-preview?file_path=${encodeURIComponent(data.filePath)}&sheet=${encodeURIComponent(sheet)}`,
                tok,
                { method: 'GET' }
            );
            onChangeData({
                activeSheet: sheet,
                columns: (result.columns ?? []).map((c: string) => ({ name: c, type: 'string' })),
                outputSchema: (result.columns ?? []).map((c: string) => ({ name: c, type: 'string' })),
                preview: result.preview ?? [],
                rowCount: result.rowCount ?? 0,
            });
            toast.success(`Switched to sheet "${sheet}" — ${result.columns?.length ?? 0} columns`);
        } catch (err: any) {
            toast.error(`Sheet load failed: ${err?.message || 'Unknown error'}`);
        } finally {
            setSheetLoading(false);
        }
    }, [token, data.filePath, onChangeData]);

    const clearFile = () => {
        onChangeData({
            filePath: undefined, fileName: undefined, fileType: undefined,
            rowCount: undefined, columns: [], outputSchema: [], preview: [],
            sheets: [], activeSheet: '',
        });
    };

    const columns: { name: string; type: string }[] = Array.isArray(data.columns) ? data.columns : [];
    const preview: Record<string, any>[] = Array.isArray(data.preview) ? data.preview : [];
    const sheets: string[] = Array.isArray(data.sheets) ? data.sheets : [];

    return (
        <div className="space-y-5">
            {/* ── Header label ───────────────────────────────────────────── */}
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider block">
                File Source
            </label>

            {/* ── No file yet — upload zone ──────────────────────────────── */}
            {!data.filePath ? (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.json,.parquet"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
                    />
                    <div
                        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer py-8 px-4 transition-all ${
                            dragOver
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleUpload(f); }}
                    >
                        {uploading ? (
                            <>
                                <div className="h-8 w-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                                <span className="text-xs text-blue-600 font-semibold">Uploading…</span>
                            </>
                        ) : (
                            <>
                                <UploadCloud className={`h-10 w-10 ${dragOver ? 'text-blue-500' : 'text-gray-300'}`} />
                                <span className="text-sm font-semibold text-gray-600">Drop a file here</span>
                                <span className="text-xs text-gray-400">CSV · Excel · JSON · Parquet</span>
                                <span className="text-[10px] text-blue-500 underline cursor-pointer">or click to browse</span>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {/* ── File header card ───────────────────────────────────── */}
                    <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-3 space-y-3">
                        {/* Title row */}
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-white border border-blue-100 shadow-sm flex-shrink-0">
                                <Icon className={`h-5 w-5 ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate" title={data.fileName}>
                                    {data.fileName}
                                </p>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide">{fileType} file</p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-100 transition-colors"
                                    title="Replace file"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Remove file"
                                    onClick={clearFile}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls,.json,.parquet"
                            className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
                        />

                        {/* Stats pills */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {columns.length > 0 && (
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                    <Table className="h-3 w-3" />
                                    {columns.length} columns
                                </span>
                            )}
                            {(data.rowCount ?? 0) > 0 && (
                                <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                    {(data.rowCount as number).toLocaleString()} rows
                                </span>
                            )}
                            {isExcel && sheets.length > 0 && (
                                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                    {sheets.length} sheet{sheets.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* ── Excel: sheet selector ──────────────────────────────── */}
                    {isExcel && sheets.length > 0 && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <FileSpreadsheet className="h-3 w-3" /> Active Sheet
                            </label>
                            <Select
                                value={data.activeSheet || sheets[0] || ''}
                                onValueChange={handleSheetChange}
                                disabled={sheetLoading}
                            >
                                <SelectTrigger className="w-full text-sm px-3 py-2">
                                    <SelectValue placeholder={sheetLoading ? 'Loading…' : 'Select a sheet…'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                    {sheets.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {sheetLoading && (
                                <p className="text-[10px] text-blue-500 flex items-center gap-1">
                                    <span className="h-3 w-3 rounded-full border border-blue-500 border-t-transparent animate-spin inline-block" />
                                    Loading sheet…
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── CSV options ────────────────────────────────────────── */}
                    {isCsv && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <Info className="h-3 w-3" /> CSV Options
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <label className="text-[10px] text-gray-500">Delimiter</label>
                                    <Select
                                        value={data.csvDelimiter || 'auto'}
                                        onValueChange={(v) => onChangeData({ csvDelimiter: v })}
                                    >
                                        <SelectTrigger className="w-full text-xs px-2 py-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                            <SelectItem value="auto">Auto-detect</SelectItem>
                                            <SelectItem value=",">Comma (,)</SelectItem>
                                            <SelectItem value="|">Pipe (|)</SelectItem>
                                            <SelectItem value="	">Tab (\t)</SelectItem>
                                            <SelectItem value=";">Semicolon (;)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] text-gray-500">Encoding</label>
                                    <Select
                                        value={data.csvEncoding || 'utf-8'}
                                        onValueChange={(v) => onChangeData({ csvEncoding: v })}
                                    >
                                        <SelectTrigger className="w-full text-xs px-2 py-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                            <SelectItem value="utf-8">UTF-8</SelectItem>
                                            <SelectItem value="latin-1">Latin-1</SelectItem>
                                            <SelectItem value="utf-16">UTF-16</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Output Schema (column list) ─────────────────────────── */}
                    {columns.length > 0 && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <Table className="h-3 w-3" /> Output Schema
                            </label>
                            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                <div className="bg-gray-50 border-b border-gray-100 px-2 py-1 grid grid-cols-2 gap-2">
                                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Column</span>
                                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Type</span>
                                </div>
                                <div className="max-h-40 overflow-y-auto divide-y divide-gray-50">
                                    {columns.map((col) => (
                                        <div key={col.name} className="grid grid-cols-2 gap-2 px-2 py-1 hover:bg-blue-50/30 transition-colors">
                                            <span className="text-[10px] text-gray-700 font-medium truncate" title={col.name}>{col.name}</span>
                                            <span className="text-[10px] text-blue-500 font-mono">{col.type || 'string'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Data Preview ────────────────────────────────────────── */}
                    {preview.length > 0 && (
                        <div className="space-y-1.5">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between text-[11px] font-semibold text-gray-600 uppercase tracking-wide hover:text-blue-600 transition-colors"
                                onClick={() => setShowPreview(!showPreview)}
                            >
                                <span className="flex items-center gap-1">
                                    <Table className="h-3 w-3" />
                                    Data Preview ({preview.length} rows)
                                </span>
                                {showPreview ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </button>
                            {showPreview && (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                                    <table className="text-[9px] w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                                {Object.keys(preview[0]).map((k) => (
                                                    <th key={k} className="px-2 py-1.5 text-left font-bold text-blue-800 whitespace-nowrap border-r border-blue-100 last:border-r-0">
                                                        {k}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.map((row, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                                                    {Object.values(row).map((v: any, j) => (
                                                        <td
                                                            key={j}
                                                            className="px-2 py-1 text-gray-700 border-r border-gray-100 last:border-r-0 whitespace-nowrap max-w-[100px] truncate"
                                                            title={String(v ?? '')}
                                                        >
                                                            {String(v ?? '')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Loaded from (friendly — no raw paths) ──────────────── */}
                    <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 flex items-center gap-2">
                        <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconColor}`} />
                        <div className="flex-1 min-w-0">
                            <p className="text-[9px] text-gray-400 font-semibold uppercase tracking-wide">Loaded from</p>
                            <p className="text-[11px] text-gray-700 font-medium truncate" title={data.fileName}>{data.fileName}</p>
                        </div>
                    </div>

                </>
            )}
        </div>
    );
};

export default FileSourceNodeConfig;
